import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface ModalBehaviorOptions {
  /** Selector inside the modal to focus first. Falls back to the first control. */
  initialFocus?: string;
  /** Required-update gates cannot be dismissed with Escape. */
  closeOnEscape?: boolean;
  /** Command palettes own Tab for their documented filter shortcut. */
  trapFocus?: boolean;
}

/**
 * The keyboard contract shared by every modal surface.
 *
 * A `role="dialog"` changes what assistive technology calls a panel; it does
 * not move focus, keep focus inside, or return focus to the opener. Those are
 * behaviours, and before this hook each dialog had implemented a different
 * subset. In several of them Tab reached the dimmed application behind the
 * modal, while closing dumped focus at the top of the document.
 *
 * `onClose` lives in a ref so an inline callback does not tear this effect down
 * on every render and accidentally replace the real opener with an element
 * inside the dialog.
 */
export function useModalBehavior<T extends HTMLElement>(
  onClose: () => void,
  {
    initialFocus,
    closeOnEscape = true,
    trapFocus = true,
  }: ModalBehaviorOptions = {},
): RefObject<T | null> {
  const containerRef = useRef<T>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const controls = () =>
      [...(containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter(
        (element) => element.getClientRects().length > 0,
      );

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !trapFocus) return;

      const focusable = controls();
      if (focusable.length === 0) {
        event.preventDefault();
        containerRef.current?.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !containerRef.current?.contains(active))) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && (active === last || !containerRef.current?.contains(active))) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    const frame = requestAnimationFrame(() => {
      const requested =
        initialFocus === undefined
          ? null
          : containerRef.current?.querySelector<HTMLElement>(initialFocus) ?? null;
      requested?.focus({ preventScroll: true });
      if (requested === null) {
        const first = controls()[0];
        (first ?? containerRef.current)?.focus({ preventScroll: true });
      }
    });

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      cancelAnimationFrame(frame);
      if (opener?.isConnected === true) opener.focus({ preventScroll: true });
    };
  }, [closeOnEscape, initialFocus, trapFocus]);

  return containerRef;
}
